import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import {
  useUpdateZeroOutRule,
  type ZeroOutRule,
} from "../../utils/portfolio-risk-concentration/api/zeroOutRules";

/**
 * Adjust one raw investor code's zero-out rule — direct write, no propose/approve (that table
 * has none, see zero_out_rules.py's module docstring).
 *
 * Adjust-only since sql/45_: every code always carries exactly 4 fixed rules (DCB/FMV x
 * Default/Delinquent), seeded automatically — there is no "+ Add Rule" any more, so `rule` is
 * always given. Day count and % of payments are always editable (every rule is THRESHOLD
 * shape now; the FLAG shape Bankruptcy used was removed along with Bankruptcy itself).
 */
export function ZeroOutRuleDialog({
  open,
  onOpenChange,
  rule,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: ZeroOutRule | null;
  onSubmitted?: (message: string) => void;
}) {
  const update = useUpdateZeroOutRule();

  const [dayCount, setDayCount] = useState("");
  const [pctOfPayments, setPctOfPayments] = useState("");
  const [status, setStatus] = useState<"ON" | "OFF">("OFF");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !rule) return;
    setError(null);
    setDayCount(rule.dayCount !== null ? String(rule.dayCount) : "");
    setPctOfPayments(rule.pctOfPayments !== null ? String(rule.pctOfPayments) : "");
    setStatus(rule.status);
  }, [open, rule]);

  async function submit() {
    if (!rule) return;
    setError(null);

    const days = Number(dayCount);
    const pct = Number(pctOfPayments);
    if (!Number.isFinite(days) || days <= 0) {
      setError("Day count must be a positive number.");
      return;
    }
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      setError("% of payments is a percent between 0 and 100 (10 for 10%, not 0.10).");
      return;
    }

    try {
      await update.mutateAsync({
        ruleId: rule.ruleId,
        dayCount: days,
        pctOfPayments: pct,
        status,
      });
      onSubmitted?.(
        `Code ${rule.bankdwnInvestorCode} · ${rule.method} · ${rule.condition} updated.`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Zero Out Rule</DialogTitle>
          <DialogDescription>
            Given a contract already passed Eligibility, does its current status zero out its
            value under this specific method.
          </DialogDescription>
        </DialogHeader>

        {rule && (
          <div className="space-y-4">
            {error && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            <div>
              <Label>Investor Code</Label>
              <p className="mt-1 text-sm text-muted-foreground">
                {rule.bankdwnInvestorCode} — {rule.facilityLabel} · {rule.method} · {rule.condition}
              </p>
            </div>

            {rule.offReason && status === "OFF" && (
              <p className="text-xs text-muted-foreground">Currently off: {rule.offReason}.</p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="zo-day-count">Day Count</Label>
                <Input
                  id="zo-day-count"
                  className="mt-1"
                  inputMode="numeric"
                  value={dayCount}
                  onChange={(e) => setDayCount(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="zo-pct">% of Payments</Label>
                <Input
                  id="zo-pct"
                  className="mt-1"
                  inputMode="decimal"
                  value={pctOfPayments}
                  onChange={(e) => setPctOfPayments(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="zo-status" className="font-normal">
                {status === "ON" ? "On" : "Off"}
              </Label>
              <Switch
                id="zo-status"
                checked={status === "ON"}
                onCheckedChange={(v) => setStatus(v ? "ON" : "OFF")}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={update.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={update.isPending || !rule} onClick={submit}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
